import { useEffect, useState } from "react";
import {
  createFileRoute,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import { meQuery } from "@/lib/queries";
import { useCreateGame } from "@/lib/mutations";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/games/create")({
  component: CreateGamePage,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me.user) {
      throw redirect({ to: "/login" });
    }
  },
});

const PRESET_ROUNDS = [5, 7, 10, 13] as const;

function emailHandle(email: string | null | undefined): string {
  if (!email) return "";
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

function CreateGamePage() {
  const router = useRouter();
  const create = useCreateGame();
  const meQ = useQuery(meQuery);
  const defaultName = emailHandle(meQ.data?.user?.email);

  const [name, setName] = useState(defaultName);
  const [rounds, setRounds] = useState<number>(7);
  const [doubleBlankPenalty, setDoubleBlankPenalty] = useState(false);

  // Backfill the name field when meQuery resolves after mount.
  useEffect(() => {
    if (defaultName && !name) setName(defaultName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultName]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (create.isPending) return;
    const trimmed = name.trim();
    try {
      const res = await create.mutateAsync({
        name: trimmed,
        rounds,
        rules: { doubleBlankPenalty },
      });
      await router.navigate({
        to: "/games/$id/lobby",
        params: { id: res.game.id },
      });
    } catch {
      // surfaced via create.error
    }
  }

  const errorMessage =
    create.error instanceof ApiError
      ? create.error.message
      : create.error
      ? "Couldn't lay the tracks. Try again."
      : null;

  return (
    <section className="mx-auto mt-10 max-w-md pb-32 md:pb-0">
      <Card>
        <CardHeader>
          <CardTitle>New game</CardTitle>
          <p className="font-mono text-xs text-muted-foreground">
            Pick a round count and house rules. You'll be the host.
          </p>
        </CardHeader>
        <CardContent>
          <form id="create-game" onSubmit={onSubmit} className="space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="name">Your name</Label>
              <Input
                id="name"
                type="text"
                autoComplete="given-name"
                maxLength={32}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Buck"
              />
              <p className="font-mono text-[11px] text-muted-foreground">
                The boys will see this on the scoreboard.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Rounds</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_ROUNDS.map((n) => {
                  const active = rounds === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRounds(n)}
                      className={cn(
                        "rounded-md border px-4 py-2 font-display text-base transition-colors",
                        "border-input bg-background/70 hover:bg-sand-100",
                        active && "border-rust-500 bg-rust-500/10 text-foreground",
                      )}
                      aria-pressed={active}
                    >
                      {n}
                    </button>
                  );
                })}
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={13}
                  value={rounds}
                  onChange={(e) => {
                    const n = Number.parseInt(e.target.value || "0", 10);
                    if (Number.isFinite(n) && n >= 1 && n <= 13) setRounds(n);
                  }}
                  className="w-20"
                  aria-label="custom rounds"
                />
              </div>
              <p className="font-mono text-[11px] text-muted-foreground">
                Number of rounds you'll play. The boys agree on the engine each round at the table.
              </p>
            </div>

            <div className="space-y-2">
              <Label>House rules</Label>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-input bg-background/70 p-3">
                <input
                  type="checkbox"
                  checked={doubleBlankPenalty}
                  onChange={(e) => setDoubleBlankPenalty(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-input accent-rust-500"
                />
                <span>
                  <span className="block font-display text-base text-foreground">
                    Double-blank counts 50
                  </span>
                  <span className="block font-mono text-xs text-muted-foreground">
                    Off: blanks score 0. On: a leftover double-blank costs 50 pips.
                  </span>
                </span>
              </label>
            </div>

            {errorMessage && (
              <div
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-sm text-destructive"
              >
                {errorMessage}
              </div>
            )}
          </form>
        </CardContent>

        <div className="mt-5 hidden md:flex md:justify-end">
          <Button
            type="submit"
            form="create-game"
            size="lg"
            disabled={create.isPending}
          >
            {create.isPending ? "Laying track…" : "Start a new game"}
          </Button>
        </div>
      </Card>

      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-border/50 bg-sand-50/95 px-4 py-3 backdrop-blur md:hidden">
        <Button
          type="submit"
          form="create-game"
          size="lg"
          disabled={create.isPending}
          className="w-full"
        >
          {create.isPending ? "Laying track…" : "Start a new game"}
        </Button>
      </div>
    </section>
  );
}
